import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { Lightbox, LightboxType } from "../../../../../../../../react-core/Html/Components/Lightbox";
import { FormComponent } from "../../../../../../../../react-core/Html/FormComponent";
import { PartLoading } from "../../../../../../../../react-core/Html/PartLoading";
import { JsonDateParser } from "../../../../../../../../react-core/Utils/JsonDateParser";
import { CurrencyCode } from "../../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { UserClaim } from "../../../../../../../../Wrap/Constants/Enums/UserClaim";
import { BouncedCharge } from "../../../../../../../../Wrap/Models/Domains/Transactions/Investments/BouncedCharge";
import { InvestmentTransactions } from "../../../../../../../../Wrap/Models/Domains/Transactions/Investments/InvestmentTransactions";
import { OrderHistory } from "../../../../../../../../Wrap/Models/Domains/Transactions/Investments/OrderHistory";
import { OrderValuation } from "../../../../../../../../Wrap/Models/Domains/Transactions/Investments/OrderValuation";
import { Permission } from "../../../../../../../Shared/Permission/Permission";
import { Table } from "../../../../../../../Shared/Table/Table";
import { TransactionAction } from "../Actions/TransactionActions";
import { Contents, InvestmentTransactionTypeMap } from "../Resource/Contents";

interface ITransactionHistoryPopupViewProps {
    showBox: boolean;
    onChange: (val: boolean) => void;
    transId: number;
    orderId: string;
}

interface ITransactionHistoryPopUpState {
    transDetail: InvestmentTransactions;
    orderList: Array<OrderValuation>;
    orderHistoryList: Array<OrderHistory>;
    disinvestChargeList: Array<BouncedCharge>;
    isTransLoading: boolean;
    isOrderLoading: boolean;
    isOrderHistoryLoading: boolean;
    isDisinvestLoading: boolean;
}

export class TransactionHistoryPopupView extends FormComponent<
    ITransactionHistoryPopupViewProps,
    ITransactionHistoryPopUpState
> {
    constructor(props: ITransactionHistoryPopupViewProps) {
        super(props);
        this.state = {
            transDetail: null,
            orderList: [],
            orderHistoryList: [],
            disinvestChargeList: [],
            isTransLoading: true,
            isOrderLoading: true,
            isOrderHistoryLoading: true,
            isDisinvestLoading: true,
        };
    }

    componentDidMount() {
        this.getData();
    }

    private getData() {
        const { orderId, transId } = this.props;
        if (!orderId) {
            TransactionAction.getTransactionsByTransId(transId, (data: InvestmentTransactions) => {
                this.setState({
                    isTransLoading: false,
                    transDetail: data,
                });
            });
        } else {
            TransactionAction.getClientTransactionOrder(Number(orderId), (callBack: Array<OrderValuation>) => {
                this.setState({
                    isOrderLoading: false,
                    orderList: callBack,
                });
            });

            TransactionAction.getTransactionOrderHistory(Number(orderId), (data: Array<OrderHistory>) => {
                this.setState({
                    isOrderHistoryLoading: false,
                    orderHistoryList: data,
                });
            });

            TransactionAction.getDisinvestCharge(Number(orderId), (data: Array<BouncedCharge>) => {
                this.setState({
                    isDisinvestLoading: false,
                    disinvestChargeList: data,
                });
            });
        }
    }

    private renderModal(): JSX.Element {
        const { orderId } = this.props;
        return (
            <Lightbox
                show={this.props.showBox}
                title={orderId ? Contents.OrderHistoryTitle : Contents.TransactionDetailsTitle}
                contentAfterTitle={
                    <Permission userClaim={UserClaim.AdviserPortalDluHelpLinkRedirectorCanView}>
                        <button className="icon help" onClick={() => this.navigateHelp()} />
                    </Permission>
                }
                onClose={() => this.props.onChange(false)}
                type={LightboxType.Modal}
                unclosable={false}
                noOverlay={false}
                noAutofocus={false}
                cssWrapper={"is-default-lightbox is-wide header-d-flex"}
            >
                {this.getModalBody()}
            </Lightbox>
        );
    }

    private getModalBody(): JSX.Element {
        const { orderId } = this.props;
        const { isOrderLoading, isOrderHistoryLoading, isDisinvestLoading, isTransLoading } = this.state;

        if (
            (orderId && (isOrderLoading || isOrderHistoryLoading || isDisinvestLoading)) ||
            (!orderId && isTransLoading)
        ) {
            return <PartLoading isLoading={true} />;
        }
        return (
            <>
                <div className="content">
                    {this.renderTransactionDetailTable()}
                    {this.renderOrderValuationTable()}
                    {this.renderOrderHistoryTable()}
                    {this.renderDisinvestChargeTable()}
                </div>
            </>
        );
    }

    private renderTransactionDetailTable() {
        const { orderId } = this.props;
        const { transDetail } = this.state;
        if (orderId) {
            return null;
        }
        return (
            <table className="data-table no-sided-padding">
                <thead>
                    <tr>
                        <th>As at</th>
                        <th>Description</th>
                        <th>Transaction source</th>
                        <th>User ID</th>
                        <th>User name</th>
                        <th>Transaction date</th>
                        <th className="cell-right">Quantity</th>
                        <th>Note</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            {transDetail.Date
                                ? JsonDateParser.getFormatDateString(
                                      JsonDateParser.toDate(transDetail.Date),
                                      "dd-MM-yyyy hh:mm",
                                      true,
                                      true
                                  ) +
                                  " " +
                                  this.getHourType(JsonDateParser.toDate(transDetail.Date))
                                : null}
                        </td>
                        <td>{transDetail.Investments}</td>
                        <td>{InvestmentTransactionTypeMap[transDetail.TransSource]}</td>
                        <td>{transDetail.UserId}</td>
                        <td>{transDetail.UserName}</td>
                        <td>
                            {transDetail.DateCreated
                                ? JsonDateParser.getFormatDateString(
                                      JsonDateParser.toDate(transDetail.TransDate),
                                      "dd-MM-yyyy hh:mm",
                                      true,
                                      true
                                  ) +
                                  " " +
                                  this.getHourType(JsonDateParser.toDate(transDetail.TransDate))
                                : null}
                        </td>
                        <td>
                            {MoneyDisplayHelper.FromatMoneyDisplay(transDetail.Quantity, CurrencyCode.GBP, false, 4)}
                        </td>
                        <td>{transDetail.Note}</td>
                    </tr>
                </tbody>
            </table>
        );
    }

    private renderOrderValuationTable() {
        const { orderId } = this.props;
        const { orderList } = this.state;
        if (!orderId || (orderList && orderList.length == 0)) {
            return null;
        }
        return (
            <>
                <h3>
                    Current order <span className="text-lowercase">{orderList[0].OrderStatus}</span>
                </h3>
                <Table
                    data={orderList}
                    cssWrapper="table-first-last-cell-padding-reset"
                    col={[
                        {
                            title: "@Status",
                            key: "Status",
                        },
                        {
                            title: "Date",
                            key: "DateCreated",
                            render: (data, row: OrderValuation, index) => {
                                if (data + "" == Contents.InvalidateDate) {
                                    return null;
                                }
                                return (
                                    JsonDateParser.getFormatDateString(
                                        JsonDateParser.toDate(row.DateCreated),
                                        "dd-MM-yyyy hh:mm",
                                        true,
                                        true
                                    ) +
                                    " " +
                                    this.getHourType(JsonDateParser.toDate(row.DateCreated))
                                );
                            },
                        },
                        {
                            title: "User",
                            key: "UserName",
                        },
                        {
                            title: "Quantity",
                            key: "OrderQuantity",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                            render: (data, row: OrderValuation, index) => {
                                if (data) {
                                    return MoneyDisplayHelper.FromatMoneyDisplay(data, CurrencyCode.GBP, false, 4);
                                } else {
                                    return "Null";
                                }
                            },
                        },
                        {
                            title: "CCY",
                            key: "OrderCurrency",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                        },
                        {
                            title: "Value",
                            key: "OrderValue",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                            render: (data, row: OrderValuation, index) => {
                                return this.getMoneyFormat(data);
                            },
                        },
                        {
                            title: "Value ex-comm",
                            key: "PlacedValueExCom",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                            render: (data, row: OrderValuation, index) => {
                                if (data) {
                                    return this.getMoneyFormat(data);
                                } else {
                                    return "na";
                                }
                            },
                        },
                        {
                            title: "Comm",
                            key: "PlacedComm",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                            render: (data, row: OrderValuation, index) => {
                                if (data) {
                                    return this.getMoneyFormat(data);
                                } else {
                                    return "na";
                                }
                            },
                        },
                        {
                            title: "Fee",
                            key: "FilledFee",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                            render: (data, row: OrderValuation, index) => {
                                if (data) {
                                    return this.getMoneyFormat(data);
                                } else {
                                    return "na";
                                }
                            },
                        },
                        {
                            title: "Comm ex-fee",
                            key: "FilledCommExFee",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                            render: (data, row: OrderValuation, index) => {
                                if (data) {
                                    return this.getMoneyFormat(data);
                                } else {
                                    return "na";
                                }
                            },
                        },
                        {
                            title: "Dilution levy",
                            key: "DilutionLevy",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                            render: (data, row: OrderValuation, index) => {
                                if (data) {
                                    return this.getMoneyFormat(data);
                                } else {
                                    return "na";
                                }
                            },
                        },
                    ]}
                />
            </>
        );
    }

    private renderOrderHistoryTable() {
        const { orderId } = this.props;
        const { orderHistoryList } = this.state;
        if (!orderId || (orderHistoryList && orderHistoryList.length == 0)) {
            return null;
        }
        return (
            <>
                <h3>Order amendment history</h3>
                <Table
                    data={orderHistoryList}
                    cssWrapper="table-first-last-cell-padding-reset"
                    col={[
                        {
                            title: "Confirmed",
                            key: "Status",
                            render: (data: string, row: OrderHistory, index) => {
                                if (data && data.toLocaleLowerCase() == "created") {
                                    return "No";
                                } else {
                                    return "Yes";
                                }
                            },
                        },
                        {
                            title: "Date",
                            key: "DateEdited",
                            render: (data, row: OrderHistory, index) => {
                                if (data + "" == Contents.InvalidateDate) {
                                    return null;
                                }
                                return (
                                    JsonDateParser.getFormatDateString(
                                        JsonDateParser.toDate(row.DateEdited),
                                        "dd-MM-yyyy hh:mm",
                                        true,
                                        true
                                    ) +
                                    " " +
                                    this.getHourType(JsonDateParser.toDate(row.DateEdited))
                                );
                            },
                        },
                        {
                            title: "User",
                            key: "DateEdited",
                            render: (data, row: OrderHistory, index) => {
                                return null;
                            },
                        },
                        {
                            title: "Order",
                            key: "OrderQuantity",
                            render: (data, row: OrderHistory, index) => {
                                return this.renderQuantity(row);
                            },
                        },
                        {
                            title: "Limit",
                            key: "OrderLimit",
                            render: (data, row: OrderHistory, index) => {
                                if (!data) {
                                    return null;
                                }
                                return MoneyDisplayHelper.FromatMoneyDisplay(data, CurrencyCode.GBP, false, 4);
                            },
                        },
                        {
                            title: "Comm",
                            key: "CommRate",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                            render: (data, row: OrderHistory, index) => {
                                return this.getMoneyFormat(data) + "%, " + this.getMoneyFormat(row.CommMin) + " min";
                            },
                        },
                        {
                            title: "Cash sett",
                            key: "OrderSettCashMethod",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                            render: (data, row: OrderHistory, index) => {
                                if (
                                    row.OrderSettCashMethod &&
                                    row.OrderSettCashMethod.toLocaleLowerCase() == "direct credit"
                                ) {
                                    return "DC " + row.OrderSettBankAccount;
                                } else {
                                    return data;
                                }
                            },
                        },
                        {
                            title: "Sett CCY",
                            key: "OrderSettCurrency",
                        },
                        {
                            title: "Location",
                            key: "OrderSettLocation",
                            render: (data: string, row: OrderHistory, index) => {
                                if (data && data.toLocaleLowerCase() == "external") {
                                    return data + " " + row.OrderSHNOverride;
                                } else {
                                    return data;
                                }
                            },
                        },
                    ]}
                />
            </>
        );
    }

    private renderDisinvestChargeTable() {
        const { orderId } = this.props;
        const { disinvestChargeList } = this.state;
        if (!orderId || (disinvestChargeList && disinvestChargeList.length == 0)) {
            return null;
        }
        return (
            <>
                <h3>Charge Details</h3>
                <Table
                    cssWrapper="table-first-last-cell-padding-reset"
                    data={disinvestChargeList}
                    col={[
                        {
                            title: "Charge type",
                            key: "ChargeDescription",
                        },
                        {
                            title: "Charge date",
                            key: "ChargeDate",
                            render: (data, row: BouncedCharge, index) => {
                                if (data + "" == Contents.InvalidateDate) {
                                    return null;
                                }
                                return (
                                    JsonDateParser.getFormatDateString(
                                        JsonDateParser.toDate(row.ChargeDate),
                                        "dd-MM-yyyy hh:mm",
                                        true,
                                        true
                                    ) +
                                    " " +
                                    this.getHourType(JsonDateParser.toDate(row.ChargeDate))
                                );
                            },
                        },
                        {
                            title: "Charge amount",
                            key: "ChargeAmount",
                            thCssWrapper: "cell-right",
                            tdCssWrapper: "cell-right",
                            render: (data, row: BouncedCharge, index) => {
                                return this.getMoneyFormat(data);
                            },
                        },
                    ]}
                />
            </>
        );
    }

    private renderQuantity(orderHistory: OrderHistory) {
        if (orderHistory.OrderQuantity) {
            return (
                orderHistory.BatchType +
                " " +
                orderHistory.OrderBuySell +
                " " +
                MoneyDisplayHelper.FromatMoneyDisplay(orderHistory.OrderQuantity, CurrencyCode.GBP, false, 4)
            );
        } else {
            return (
                orderHistory.BatchType +
                " " +
                orderHistory.OrderBuySell +
                " " +
                orderHistory.OrderCurrency +
                " " +
                this.getMoneyFormat(orderHistory.OrderValue) +
                " Comm " +
                orderHistory.CommBasis
            );
        }
    }

    private navigateHelp() {
        window.open(
            "/Popups/HelpLinkRedirector.aspx?redirecturl=help.standardlifewrap.com",
            "Details",
            "left=10,top=10,width=900,height=600,menubar=no,status=no,location=no,toolbar=no,scrollbars=yes"
        );
        return false;
    }

    private getMoneyFormat(val: number): string {
        const formatMoney = MoneyDisplayHelper.FromatMoneyDisplay(val, CurrencyCode.GBP, false, 2);
        return formatMoney;
    }

    private getHourType(date: Date) {
        let h = date.getHours();
        let str;
        if (h > 12) {
            h -= 12;
            date.setHours(h);
            str = " PM";
        } else {
            str = " AM";
        }
        return str;
    }

    public render(): JSX.Element {
        return <>{this.props.showBox ? this.renderModal() : null}</>;
    }
}
